import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useSiteContent } from '../hooks/useSiteContent';

interface Section {
  eyebrow: string;
  headingKey: string;
  headingFallback: string;
  bodyKey: string;
  bodyFallback: string;
}

const SECTIONS: Section[] = [
  {
    eyebrow: 'Admissions test',
    headingKey: 'oxbridge_tara_h',
    headingFallback: 'TARA',
    bodyKey: 'oxbridge_tara_body',
    bodyFallback:
      "For the PPE Admissions Assessment. I've sat this test and know where it gets hard: critical reasoning, economics multiple-choice, and managing time under pressure. Sessions cover past papers, specific question types, and the reading that actually helps.",
  },
  {
    eyebrow: 'Interviews',
    headingKey: 'oxbridge_interview_h',
    headingFallback: 'Interview preparation',
    bodyKey: 'oxbridge_interview_body',
    bodyFallback:
      "I sat three Oxford interviews in December 2025. I know what it feels like to be asked something you've never thought about before and have to reason out loud. Sessions are mock interviews in the relevant format, followed by honest feedback on where your reasoning held up and where it didn't.",
  },
  {
    eyebrow: 'Written work',
    headingKey: 'oxbridge_personal_h',
    headingFallback: 'Personal statements and written work',
    bodyKey: 'oxbridge_personal_body',
    bodyFallback:
      "For subjects where the written submission matters. I'll read your draft, push back on weak arguments, and help you sound like someone an admissions tutor would want to spend an hour interviewing.",
  },
];

const CREDENTIALS: { label: string; body: string }[] = [
  {
    label: 'Oxford offer',
    body: 'Hertford College, PPE offer-holder.',
  },
  {
    label: 'Interview experience',
    body: 'Three Oxford interviews sat in December 2025.',
  },
  {
    label: 'John Locke Institute',
    body: 'Summer School High Commendation, 2023 and 2024.',
  },
];

const OxbridgePage: React.FC = () => {
  const c = useSiteContent();

  return (
    <main className="bg-cream">
      <Helmet>
        <title>Oxbridge and university prep — EJL Education</title>
        <meta name="description" content="TARA preparation, mock interviews, and personal statement review from an Oxford PPE offer-holder." />
      </Helmet>
      <section className="section">
        <div className="container-content">
          <h1 className="mb-6">
            {c('oxbridge_heading', 'Oxbridge and competitive university prep')}
          </h1>
          <p className="text-body text-ink max-w-2xl">
            {c(
              'oxbridge_intro',
              "I got an offer from Hertford College, Oxford to read PPE. I can help you do the same, or tell you honestly if the application doesn't look realistic yet. Either way, we start with your current writing and thinking, not a template."
            )}
          </p>
        </div>
      </section>

      <section className="section pt-0">
        <div className="container-content">
          {SECTIONS.map((s, i) => (
            <div
              key={s.headingKey}
              className={i === 0 ? '' : 'border-t border-rule pt-12 mt-12'}
            >
              <p className="eyebrow mb-4">{s.eyebrow}</p>
              <h2 className="mb-6">{c(s.headingKey, s.headingFallback)}</h2>
              <p className="text-body text-ink max-w-2xl">
                {c(s.bodyKey, s.bodyFallback)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-rule py-16 md:py-20">
        <div className="container-content">
          <p className="eyebrow mb-6">Relevant experience</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-10 gap-y-8">
            {CREDENTIALS.map((item) => (
              <div key={item.label}>
                <p className="eyebrow mb-3">{item.label}</p>
                <p className="text-body text-muted">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-rule py-16 md:py-20">
        <div className="container-content">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <p className="text-body text-ink max-w-2xl">
              Want to talk through your application? A 20-minute intro call is £5.
            </p>
            <Link to="/contact" className="btn btn-primary self-start md:self-auto">
              Book £5 intro call
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default OxbridgePage;
